import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Clock, AlertTriangle, Loader2, Edit2, X, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const MyPendingPage = () => {
  const { user } = useAuth();
  const [pendingImages, setPendingImages] = useState([]);
  const [rejectedImages, setRejectedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, image: null });
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/tags`)
      .then(({ data }) => setAvailableTags(data.tags || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      fetchMyPhotos();
    }
  }, [user]);

  const fetchMyPhotos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/users/me/photos`, {
        withCredentials: true
      });
      setPendingImages(response.data.pending_images || []);
      setRejectedImages(response.data.rejected_images || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (image) => {
    setEditForm({
      title: image.title || '',
      description: image.description || '',
      cats_name: (image.tags && image.tags.length ? image.tags.join(', ') : ''),
      cam_model: image.cam_model || '',
      cam_lens: image.cam_lens || '',
      cam_aperture: image.cam_aperture || '',
      cam_exp: image.cam_exp || '',
      cam_iso: image.cam_iso || '',
      cam_foc: image.cam_foc || ''
    });
    setEditModal({ open: true, image });
  };

  const handleEditSubmit = async () => {
    if (!editModal.image) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE}/images/${editModal.image.id}`,
        editForm,
        { withCredentials: true }
      );
      
      // Refresh data
      await fetchMyPhotos();
      setEditModal({ open: false, image: null });
      
      if (response.data.resubmitted) {
        alert('Photo has been resubmitted for approval!');
      }
    } catch (error) {
      console.error('Error updating image:', error);
      alert(error.response?.data?.detail || 'Failed to update image');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  const ImageCard = ({ image, status }) => (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
      <div className="aspect-video relative">
        <img
          src={image.image_urls?.thumbnail}
          alt={image.title}
          className={`w-full h-full object-cover ${status === 'rejected' ? 'opacity-60' : 'opacity-70'}`}
        />
        <div className={`absolute inset-0 flex items-center justify-center ${
          status === 'pending' ? 'bg-yellow-900/50' : 'bg-red-900/50'
        }`}>
          {status === 'pending' ? (
            <div className="text-center text-white">
              <Clock className="w-8 h-8 mx-auto mb-2" />
              <span className="font-medium">Pending Approval</span>
            </div>
          ) : (
            <div className="text-center text-white">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <span className="font-medium">Needs Changes</span>
            </div>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1">{image.title}</h3>
        <p className="text-sm text-gray-500 mb-3">
          Uploaded {new Date(image.date_published).toLocaleDateString()}
        </p>
        
        {status === 'rejected' && image.rejection_reason && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800">Admin Feedback:</p>
            <p className="text-sm text-red-700">{image.rejection_reason}</p>
          </div>
        )}
        
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => openEditModal(image)}
        >
          <Edit2 className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Clock className="w-8 h-8 mr-3 text-yellow-500" />
            My Pending Photos
          </h1>
          <p className="text-gray-600 mt-2">
            Photos waiting for approval or needing changes
          </p>
          <Link to="/upload" className="inline-flex items-center mt-3 text-blue-600 hover:text-blue-800 font-medium">
            <Upload className="w-4 h-4 mr-1" />
            Upload another photo
          </Link>
        </div>

        {/* Pending Section */}
        {pendingImages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-yellow-500" />
              Awaiting Approval ({pendingImages.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingImages.map(image => (
                <ImageCard key={image.id} image={image} status="pending" />
              ))}
            </div>
          </div>
        )}

        {/* Rejected Section */}
        {rejectedImages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              Needs Changes ({rejectedImages.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rejectedImages.map(image => (
                <ImageCard key={image.id} image={image} status="rejected" />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {pendingImages.length === 0 && rejectedImages.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No pending photos</h2>
            <p className="text-gray-600 mb-4">
              All your photos have been approved!
            </p>
            <Link to="/upload">
              <Button>Upload a New Photo</Button>
            </Link>
          </div>
        )}

        {/* Edit Modal */}
        {editModal.open && editModal.image && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Photo</h3>
                <button
                  onClick={() => setEditModal({ open: false, image: null })}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <img
                src={editModal.image.image_urls?.thumbnail}
                alt={editModal.image.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              
              {editModal.image.rejection_reason && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Admin Feedback:</p>
                  <p className="text-sm text-red-700">{editModal.image.rejection_reason}</p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 grid grid-cols-2 gap-1" data-testid="edit-tags-list">
                    {availableTags.map((tag) => {
                      const selected = (editForm.cats_name || '').split(',').map((t) => t.trim()).filter(Boolean);
                      const isChecked = selected.includes(tag.cat_name);
                      return (
                        <label key={tag.cat_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const next = isChecked
                                ? selected.filter((t) => t !== tag.cat_name)
                                : [...selected, tag.cat_name];
                              setEditForm({ ...editForm, cats_name: next.join(', ') });
                            }}
                            data-testid={`edit-tag-${tag.cat_id}`}
                          />
                          <span className="text-gray-700">{tag.cat_name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {(editForm.cats_name || '').trim() ? `Selected: ${editForm.cats_name}` : 'Pick at least one tag from the list.'}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Camera</label>
                    <Input
                      value={editForm.cam_model}
                      onChange={(e) => setEditForm({ ...editForm, cam_model: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lens</label>
                    <Input
                      value={editForm.cam_lens}
                      onChange={(e) => setEditForm({ ...editForm, cam_lens: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setEditModal({ open: false, image: null })}>
                  Cancel
                </Button>
                <Button onClick={handleEditSubmit} disabled={saving || !editForm.title}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editModal.image.published === 2 ? 'Save & Resubmit' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPendingPage;
